import { useRouter } from 'next/router'

import styled from 'styled-components'

import { PM_MERGE_TAGS } from 'shared/common/enums'
import { Nullable } from 'shared/utils/type-guards'

import { COLORS, SIZES } from '~/common/globalStyles'
import { MergeTag as MergeTagBase, MergeTaggable } from '~/common/types/graphql'
import { parsers } from '~/utils/parsers'
import LoadingHandler from '~/views/components/LoadingHandler'
import Link from '~/views/components/routing/Link'
import Avatar from '~/views/components/ui/Avatar'
import RichTextView from '~/views/components/ui/RichTextView'
import Text from '~/views/components/ui/Text'
import { useCurrentUserContext } from '~/views/contexts/CurrentUserContext'
import useMergeTags from '~/views/hooks/useMergeTags'

import { MergeTagContext } from 'shared/gen/manager/core/proto/merge-tags'
import { useHydrateMergeTags } from 'shared/hooks/use-hydrate-merge-tags'
import { useWorkspaceFeatureFlag } from 'shared/services/feature-flags/hooks'

import HydratedDescription from './HydratedDescription'
import { parseDescriptionToHtml } from './utils/description'
import { useStaticLabels } from 'shared/hooks/use-static-labels'

const VideoWrapper = styled.div`
  position: relative;
  height: 56.25%;
  min-height: 210px;
  margin-right: 1px;
`

const ExperienceOverviewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`

const CustomIframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

const AvatarWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`
export interface Props {
  experienceOverview?: Nullable<string>
  imageUrl?: Nullable<string>
  isTemplate?: boolean
  overviewType: string
  videoUrl?: Nullable<string>
  organization?: Nullable<{
    id: string
    name?: Nullable<string>
    logo?: Nullable<{ originalUrl?: Nullable<string> }>
  }>
  projectManager?: Nullable<{
    id: string
  }>
  projectId?: string
}

interface MergeTag extends MergeTagBase {
  displayName: string
  key: string
}

type ExperienceOverViewTextProps = Pick<
  Props,
  'experienceOverview' | 'isTemplate'
> & {
  projectId?: string
  isUnifiedMergeTagsEnabled: boolean
}

const ExperienceOverviewText = ({
  projectId,
  isTemplate,
  experienceOverview,
  isUnifiedMergeTagsEnabled,
}: ExperienceOverViewTextProps) => {
  const { mergeTagValues, mergeTagValuesLoading } = useMergeTags({
    skip: isUnifiedMergeTagsEnabled,
    taggable: MergeTaggable.Template,
    withValues: true,
  })

  const mergeTags = isTemplate ? (PM_MERGE_TAGS as MergeTag[]) : undefined

  const shouldSkipHydration = !isUnifiedMergeTagsEnabled || !projectId

  const htmlParsedDescription = parseDescriptionToHtml(experienceOverview || '')

  const { data: hydratedDescription, loading: loadingHydratedDescription } =
    useHydrateMergeTags({
      skip: shouldSkipHydration,
      unitId: projectId || '',
      templateText: htmlParsedDescription || '',
      mergeTagContext: MergeTagContext.PROJECT,
    })

  const isLoadingMergeTags = mergeTagValuesLoading || loadingHydratedDescription
  const waitingFor = isUnifiedMergeTagsEnabled
    ? hydratedDescription
    : mergeTagValues

  return (
    <LoadingHandler loading={isLoadingMergeTags} waitingFor={waitingFor}>
      {(mergeTagsValues: MergeTag[]) => {
        return isUnifiedMergeTagsEnabled ? (
          <HydratedDescription
            isLoading={loadingHydratedDescription}
            dataTestId="experience-overview-text"
            description={hydratedDescription}
          />
        ) : (
          <RichTextView
            color={COLORS.darkGray as 'darkGray'}
            value={experienceOverview}
            mergeTags={mergeTags}
            dataTestId="experience-overview-text"
            mergeValues={mergeTagsValues}
          />
        )
      }}
    </LoadingHandler>
  )
}

const ExperienceOverview = ({
  projectManager,
  videoUrl: videoUrlProp,
  overviewType,
  experienceOverview,
  imageUrl,
  organization,
  projectId,
}: Props) => {
  const [getProjectOverviewLabel] = useStaticLabels(['projectOverview'])
  const { currentUser } = useCurrentUserContext()

  const isUnifiedMergeTagsEnabled = useWorkspaceFeatureFlag(
    'ENG-5851_Unified_Merge_Tags',
    undefined,
    organization?.id,
  )

  const router = useRouter()
  const videoId = videoUrlProp ? parsers.youtubeIdParser(videoUrlProp) : null
  const embeddableVideoUrl =
    videoId && `https://www.youtube.com/embed/${videoId}?rel=0&amp;showinfo=0`
  const editReqString = `edit-${
    overviewType === 'template' ? 'template' : 'templates'
  }`

  const couldAddExperienceOverview =
    organization?.id === currentUser?.organization?.id &&
    (currentUser?.isAdmin() ||
      currentUser?.isManager() ||
      currentUser?.id === projectManager?.id)

  return (
    <ExperienceOverviewWrapper>
      <VideoWrapper>
        {embeddableVideoUrl ? (
          <CustomIframe
            data-testid="youtube-video"
            title="videoUrl"
            src={embeddableVideoUrl}
            frameBorder="0"
            allowFullScreen
          />
        ) : (
          <AvatarWrapper>
            {imageUrl ? (
              <Avatar
                size="100%"
                imageUrl={imageUrl}
                style={{ backgroundSize: 'contain' }}
                name1={organization?.name || ''}
                square
              />
            ) : organization && organization.logo ? (
              <Avatar
                data-testid="organization-logo"
                size="200px"
                name1={organization.name || ''}
                fontSize={SIZES.mega}
                imageUrl={organization.logo.originalUrl || ''}
                style={{ backgroundSize: 'contain' }}
                square
              />
            ) : (
              <div>
                <Link
                  href={{
                    query: {
                      ...router.query,
                      [editReqString]: true,
                    },
                  }}
                >
                  <Text color="lightGray" weight="bold" size="xl">
                    {getProjectOverviewLabel('projectOverviewAddVideoImage')}
                  </Text>
                </Link>
              </div>
            )}
          </AvatarWrapper>
        )}
      </VideoWrapper>
      <div style={{ marginTop: SIZES.medium }}>
        <Text
          size="medium"
          style={
            experienceOverview
              ? {}
              : { display: 'inline-block', textAlign: 'center', width: '100%' }
          }
        >
          {experienceOverview ? (
            <ExperienceOverviewText
              projectId={projectId}
              isTemplate={overviewType === 'template'}
              experienceOverview={experienceOverview}
              isUnifiedMergeTagsEnabled={isUnifiedMergeTagsEnabled}
            />
          ) : couldAddExperienceOverview ? (
            <Link
              href={{
                query: { ...router.query, [editReqString]: true },
              }}
            >
              <Text color="lightGray" size="large">
                {getProjectOverviewLabel('projectOverviewAddOverview')}
              </Text>
            </Link>
          ) : (
            <></>
          )}
        </Text>
      </div>
    </ExperienceOverviewWrapper>
  )
}

export default ExperienceOverview
